import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { SmallLoader } from 'shared/components';

import {
  InlineChainDispensarySelect,
  useChainDispensaries,
  useChainDispensarySelect,
} from 'src/components/chain-dispensary-select';

export type InlineChainLocationSelectorProps = {
  /** The chain ID to fetch dispensaries for */
  chainId: string;
  /** Initial selected dispensary IDs */
  initialSelectedIds?: string[];
  /** Callback when selection changes */
  onSelectionChange: (selectedIds: string[]) => void;
  /** Optional dispensary ID that must always be selected (e.g., current dispensary) */
  requiredDispensaryId?: string;
  /** Error message to show if required dispensary is deselected */
  requiredDispensaryErrorMessage?: string;
  /** Callback for showing error messages */
  onError?: (message: string) => void;
  /** Dispensary IDs to exclude from the list (e.g., source dispensary when copying) */
  excludeDispensaryIds?: string[];
  /** Maximum height of the scrollable list area. Defaults to 400px */
  maxHeight?: number;
  /** Whether to show the header with selected count and select all button */
  showHeader?: boolean;
};

/**
 * An inline location selector component that displays chain dispensaries
 * in a scrollable list instead of a dropdown. Ideal for modals where users
 * need to select from hundreds of locations.
 *
 * This component handles:
 * - Fetching chain dispensaries data
 * - Managing selection state
 * - Optional required dispensary validation
 */
export function InlineChainLocationSelector({
  chainId,
  initialSelectedIds = [],
  onSelectionChange,
  requiredDispensaryId,
  requiredDispensaryErrorMessage = 'This location must remain selected.',
  onError,
  excludeDispensaryIds = [],
  maxHeight = 400,
  showHeader = true,
}: InlineChainLocationSelectorProps): JSX.Element {
  const { chainDispensaries, loading, error } = useChainDispensaries({ chainId });
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);
  const hasInitializedRef = useRef(false);

  // Filter out excluded dispensaries
  const filteredDispensaries = React.useMemo(() => {
    if (!chainDispensaries) {
      return null;
    }
    if (excludeDispensaryIds.length === 0) {
      return chainDispensaries;
    }
    return chainDispensaries.filter((d) => !excludeDispensaryIds.includes(d.id));
  }, [chainDispensaries, excludeDispensaryIds]);

  // Update selected IDs when initial values change (but not on first render)
  useEffect(() => {
    if (hasInitializedRef.current && initialSelectedIds.length > 0) {
      setSelectedIds(initialSelectedIds);
    }
  }, [initialSelectedIds]);

  // Initialize with all dispensaries selected when data loads (only once)
  useEffect(() => {
    if (
      !hasInitializedRef.current &&
      filteredDispensaries &&
      filteredDispensaries.length > 0 &&
      initialSelectedIds.length === 0
    ) {
      hasInitializedRef.current = true;
      const allIds = filteredDispensaries.map((d) => d.id);
      setSelectedIds(allIds);
      onSelectionChange(allIds);
    }
  }, [filteredDispensaries, initialSelectedIds.length, onSelectionChange]);

  const chainDispensarySelect = useChainDispensarySelect(
    filteredDispensaries ?? [],
    selectedIds,
    (newSelectedIds) => {
      // Validate required dispensary if specified
      if (requiredDispensaryId && !newSelectedIds.includes(requiredDispensaryId)) {
        onError?.(requiredDispensaryErrorMessage);
        return;
      }

      setSelectedIds(newSelectedIds);
      onSelectionChange(newSelectedIds);
    },
    { allowEmptySelection: !requiredDispensaryId }
  );

  if (loading || filteredDispensaries === null) {
    return (
      <LoaderContainer>
        <SmallLoader aria-label='Loading locations' height={20} />
      </LoaderContainer>
    );
  }

  if (error) {
    return <ErrorText>Failed to load locations. Please try again.</ErrorText>;
  }

  if (filteredDispensaries.length === 0) {
    return <EmptyText>No locations found.</EmptyText>;
  }

  return (
    <InlineChainDispensarySelect
      {...chainDispensarySelect}
      options={filteredDispensaries}
      maxHeight={maxHeight}
      showHeader={showHeader}
    />
  );
}

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
`;

const ErrorText = styled.span`
  color: ${({ theme }) => theme.colors.red};
  font-size: 13px;
`;

const EmptyText = styled.span`
  color: ${({ theme }) => theme.colors.grey};
  font-size: 13px;
`;
